import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import { supabase } from '../../lib/supabase'
import { Header } from '../Layout/Header'
import {
  CheckCircle, Circle, Trash2, Plus, Clock, AlertTriangle, X, Wrench,
  ShieldAlert, RefreshCw, Search, Power, PowerOff, Image as ImageIcon,
} from 'lucide-react'

interface Task {
  id: string
  task: string
  completed: boolean
  scheduled_at?: string
  created_at: string
  created_by: string
  created_by_name: string
  assigned_to?: string
  assigned_to_name?: string
  completed_at?: string
  priority?: string
}

interface Machine {
  id: string
  name: string
  status: string
  hal?: string
  location?: string
}

interface Problem {
  id: string
  datum_tijd: string
  productcode?: string
  machine_id?: string
  argumentatie?: string
  oplossing_gevonden: boolean
  oplossing_omschrijving?: string
  fotos?: any
  gebruiker_naam?: string
  created_by_name?: string
}

type MachineStatus = 'active' | 'maintenance' | 'inactive'

const STATUS_META: Record<string, { label: string; dot: string; badge: string }> = {
  active:      { label: 'In bedrijf',    dot: 'bg-green-500',  badge: 'badge badge-green' },
  maintenance: { label: 'Onderhoud',     dot: 'bg-orange-500', badge: 'badge badge-yellow' },
  inactive:    { label: 'Buiten dienst', dot: 'bg-gray-400',   badge: 'badge badge-gray' },
}

const statusMeta = (status?: string) => STATUS_META[status || 'active'] ?? STATUS_META.active

const STATUS_OPTIONS: Array<{ val: MachineStatus; label: string; icon: React.ElementType; activeClass: string }> = [
  { val: 'active',      label: 'In bedrijf',    icon: Power,    activeClass: 'bg-green-600 text-white' },
  { val: 'maintenance', label: 'Onderhoud',     icon: Wrench,   activeClass: 'bg-orange-500 text-white' },
  { val: 'inactive',    label: 'Buiten dienst', icon: PowerOff, activeClass: 'bg-gray-600 text-white' },
]

export const TechnicalDashboard: React.FC = () => {
  const { user, logout } = useAuth()
  const navigate = useNavigate()

  const [problems, setProblems] = useState<Problem[]>([])
  const [machines, setMachines] = useState<Machine[]>([])
  const [tasks, setTasks] = useState<Task[]>([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [search, setSearch] = useState('')

  // Storing oplossen
  const [solving, setSolving] = useState<Problem | null>(null)
  const [solution, setSolution] = useState('')
  const [solutionSaving, setSolutionSaving] = useState(false)
  const [solutionError, setSolutionError] = useState<string | null>(null)

  // Machinestatus
  const [statusSavingId, setStatusSavingId] = useState<string | null>(null)
  const [statusError, setStatusError] = useState<string | null>(null)

  // Eigen taken
  const [showTaskForm, setShowTaskForm] = useState(false)
  const [newTask, setNewTask] = useState('')
  const [scheduledTime, setScheduledTime] = useState('')
  const [newTaskPriority, setNewTaskPriority] = useState<'laag' | 'normaal' | 'hoog'>('normaal')

  const loadAll = async () => {
    const problemReq = supabase
      .from('probleem_meldingen').select('*')
      .order('datum_tijd', { ascending: false })
      .limit(200)
    const machineReq = supabase.from('machines').select('id, name, status, hal, location').order('name')
    const taskReq = user?.id
      ? supabase.from('tasks').select('*')
          .or(`assigned_to.eq.${user.id},created_by.eq.${user.id}`)
          .order('created_at', { ascending: false })
      : Promise.resolve({ data: [], error: null })

    const [problemRes, machineRes, taskRes] = await Promise.all([problemReq, machineReq, taskReq])
    if (!problemRes.error) setProblems(problemRes.data || [])
    if (!machineRes.error) setMachines(machineRes.data || [])
    if (!(taskRes as any).error) setTasks((taskRes as any).data || [])
    setLoading(false)
  }

  useEffect(() => {
    loadAll()
    const channel = supabase.channel('technical-dashboard')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'probleem_meldingen' }, () => loadAll())
      .subscribe()
    return () => { channel.unsubscribe() }
  }, [user?.id])

  const refresh = async () => {
    setRefreshing(true)
    await loadAll()
    setRefreshing(false)
  }

  // ─── Storingen ──────────────────────────────────────────────────────
  const openSolveModal = (problem: Problem) => {
    setSolving(problem)
    setSolution(problem.oplossing_omschrijving || '')
    setSolutionError(null)
  }

  const saveSolution = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!solving || !solution.trim()) return
    setSolutionSaving(true)
    setSolutionError(null)
    try {
      const { error } = await supabase.from('probleem_meldingen').update({
        oplossing_gevonden: true,
        oplossing_omschrijving: solution.trim(),
      }).eq('id', solving.id)
      if (error) {
        setSolutionError(error.message || 'Opslaan mislukt.')
        return
      }
      setSolving(null)
      setSolution('')
      await loadAll()
    } finally {
      setSolutionSaving(false)
    }
  }

  const reopenProblem = async (problem: Problem) => {
    await supabase.from('probleem_meldingen').update({ oplossing_gevonden: false }).eq('id', problem.id)
    loadAll()
  }

  // ─── Machinestatus ──────────────────────────────────────────────────
  const setMachineStatus = async (machine: Machine, status: MachineStatus) => {
    if (machine.status === status) return
    setStatusSavingId(machine.id)
    setStatusError(null)
    const { error } = await supabase.from('machines').update({ status }).eq('id', machine.id)
    if (error) setStatusError(`Status van ${machine.name} kon niet worden opgeslagen.`)
    else setMachines(prev => prev.map(m => (m.id === machine.id ? { ...m, status } : m)))
    setStatusSavingId(null)
  }

  // ─── Taken ──────────────────────────────────────────────────────────
  const addTask = async () => {
    if (!newTask.trim()) return
    const { error } = await supabase.from('tasks').insert([{
      task: newTask.trim(),
      completed: false,
      scheduled_at: scheduledTime ? new Date(`${new Date().toDateString()} ${scheduledTime}`).toISOString() : null,
      created_by: user?.id,
      created_by_name: user?.name || 'Onbekend',
      assigned_to: user?.id,
      assigned_to_name: user?.name,
      priority: newTaskPriority,
    }])
    if (!error) {
      setNewTask('')
      setScheduledTime('')
      setNewTaskPriority('normaal')
      setShowTaskForm(false)
      loadAll()
    }
  }

  const toggleTask = async (taskId: string, currentStatus: boolean) => {
    await supabase.from('tasks').update({
      completed: !currentStatus,
      completed_at: !currentStatus ? new Date().toISOString() : null,
    }).eq('id', taskId)
    loadAll()
  }

  const deleteTask = async (taskId: string) => {
    await supabase.from('tasks').delete().eq('id', taskId)
    loadAll()
  }

  const handleLogout = () => { logout(); navigate('/login') }

  // ─── Afgeleide waarden ──────────────────────────────────────────────
  // Een melding zonder machine is iets anders dan een melding met een machine die weg is.
  const machineName = (id?: string) =>
    !id ? 'Geen machine opgegeven' : (machines.find(m => m.id === id)?.name || 'Onbekende machine')
  const machineHal = (id?: string) => machines.find(m => m.id === id)?.hal

  const openProblems = problems.filter(p => !p.oplossing_gevonden)
  const solvedProblems = problems.filter(p => p.oplossing_gevonden)
  const today = new Date().toISOString().split('T')[0]
  const solvedToday = solvedProblems.filter(p => p.datum_tijd?.startsWith(today)).length
  const inMaintenance = machines.filter(m => m.status === 'maintenance').length
  const pendingTasks = tasks.filter(t => !t.completed)
  const completedTasks = tasks.filter(t => t.completed)

  const q = search.trim().toLowerCase()
  const filteredOpen = q
    ? openProblems.filter(p =>
        (p.argumentatie || '').toLowerCase().includes(q) ||
        (p.productcode || '').toLowerCase().includes(q) ||
        machineName(p.machine_id).toLowerCase().includes(q))
    : openProblems

  const photoCount = (fotos: any) => (Array.isArray(fotos) ? fotos.length : 0)

  const ageInHours = (iso: string) => Math.floor((Date.now() - new Date(iso).getTime()) / 3600000)

  const kpis = [
    { label: 'Open storingen',    value: openProblems.length, icon: ShieldAlert, tone: 'red' },
    { label: 'Opgelost vandaag',  value: solvedToday,         icon: CheckCircle, tone: 'green' },
    { label: 'In onderhoud',      value: inMaintenance,       icon: Wrench,      tone: 'amber' },
    { label: 'Mijn open taken',   value: pendingTasks.length, icon: Clock,       tone: 'blue' },
  ]

  const priorityStyle = (priority?: string) => {
    switch (priority) {
      case 'hoog': return 'border-l-4 border-l-red-500 bg-red-50 dark:bg-red-900/10'
      case 'laag': return 'border-l-4 border-l-gray-300 bg-gray-50 dark:bg-gray-800'
      default:     return 'border-l-4 border-l-yellow-400 bg-yellow-50 dark:bg-yellow-900/10'
    }
  }

  return (
    <div className="min-h-screen flex flex-col items-center overflow-x-hidden" style={{ background: 'var(--bg)' }}>
      <Header title="Technische dienst" onLogout={handleLogout} />

      <section className="w-full max-w-5xl px-3 sm:px-6 mt-6 sm:mt-8">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="eyebrow">Onderhoud · technische dienst</p>
            <h2 className="page-title mt-2">Goedendag, {user?.name?.split(' ')[0] || 'collega'}</h2>
            <p className="page-subtitle mt-1">Openstaande storingen, machinestatus en je eigen onderhoudstaken.</p>
          </div>
          <button onClick={refresh} className="btn-secondary touch-target" disabled={refreshing}>
            <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} /> Vernieuwen
          </button>
        </div>
      </section>

      {/* KPI's */}
      <div className="w-full max-w-5xl px-3 sm:px-6 mt-5">
        <div className="grid grid-cols-2 xl:grid-cols-4 gap-2.5 sm:gap-3">
          {kpis.map(kpi => {
            const Icon = kpi.icon
            return (
              <div key={kpi.label} className="metric-card">
                <div className={`metric-icon metric-icon-${kpi.tone}`}><Icon size={18} /></div>
                <div className="min-w-0">
                  <div className="text-[11px] sm:text-xs font-medium truncate" style={{ color: 'var(--text-secondary)' }}>{kpi.label}</div>
                  <div className="text-2xl sm:text-[28px] leading-tight font-semibold tracking-[-0.03em] mt-1" style={{ color: 'var(--text-primary)' }}>
                    {loading ? '–' : kpi.value}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Storingen */}
      <div className="w-full max-w-5xl px-3 sm:px-6 mt-4">
        <section className="card p-4 sm:p-6">
          <div className="flex flex-wrap gap-3 justify-between items-center border-b pb-3 mb-4" style={{ borderColor: 'var(--border)' }}>
            <h2 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
              Storingen ({openProblems.length} open)
            </h2>
            <div className="relative">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-tertiary)' }} />
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Zoek machine, code of tekst"
                className="input-field !py-2 !pl-9 !w-auto min-w-[220px]"
              />
            </div>
          </div>

          {loading ? (
            <div className="text-center py-8" style={{ color: 'var(--text-secondary)' }}>Laden...</div>
          ) : filteredOpen.length === 0 ? (
            <div className="text-center py-8" style={{ color: 'var(--text-secondary)' }}>
              <CheckCircle size={48} className="mx-auto mb-2 text-green-400" />
              <p>{openProblems.length === 0 ? 'Geen openstaande storingen.' : 'Geen storingen die aan je zoekopdracht voldoen.'}</p>
            </div>
          ) : (
            <div className="space-y-2.5">
              {filteredOpen.map(problem => {
                const hours = ageInHours(problem.datum_tijd)
                const urgent = hours >= 24
                return (
                  <div key={problem.id}
                    className={`p-3.5 rounded-xl border-l-4 ${urgent ? 'border-l-red-500 bg-red-50 dark:bg-red-900/10' : 'border-l-orange-400 bg-orange-50 dark:bg-orange-900/10'}`}>
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>
                            {machineName(problem.machine_id)}
                          </span>
                          {machineHal(problem.machine_id) && (
                            <span className="badge badge-gray">{machineHal(problem.machine_id)}</span>
                          )}
                          {problem.productcode && (
                            <span className="badge badge-gray">Code {problem.productcode}</span>
                          )}
                          {urgent && <span className="badge badge-red">&gt; 24 uur open</span>}
                          {photoCount(problem.fotos) > 0 && (
                            <span className="machine-stat"><ImageIcon size={11} /> {photoCount(problem.fotos)}</span>
                          )}
                        </div>
                        <p className="text-sm mt-1.5 whitespace-pre-wrap" style={{ color: 'var(--text-primary)' }}>
                          {problem.argumentatie || '(geen omschrijving)'}
                        </p>
                        <div className="text-xs mt-1.5 flex items-center gap-3 flex-wrap" style={{ color: 'var(--text-secondary)' }}>
                          <span className="flex items-center gap-1">
                            <Clock size={12} /> {new Date(problem.datum_tijd).toLocaleString('nl-NL')}
                          </span>
                          <span>Gemeld door {problem.gebruiker_naam || problem.created_by_name || 'onbekend'}</span>
                        </div>
                      </div>
                      <button onClick={() => openSolveModal(problem)} className="btn-primary !px-4 touch-target shrink-0">
                        <Wrench size={16} /> Oplossen
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>
          )}

          {solvedProblems.length > 0 && (
            <details className="mt-4">
              <summary className="cursor-pointer text-sm py-2" style={{ color: 'var(--text-secondary)' }}>
                {solvedProblems.length} opgeloste {solvedProblems.length === 1 ? 'storing' : 'storingen'} tonen
              </summary>
              <div className="mt-2 space-y-2">
                {solvedProblems.map(problem => (
                  <div key={problem.id} className="p-3 rounded-xl bg-green-50 border-l-4 border-l-green-400 dark:bg-green-900/10">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-medium text-sm" style={{ color: 'var(--text-primary)' }}>
                          {machineName(problem.machine_id)}
                        </div>
                        <p className="text-sm mt-0.5" style={{ color: 'var(--text-secondary)' }}>{problem.argumentatie}</p>
                        {problem.oplossing_omschrijving && (
                          <p className="text-sm mt-1.5 pl-3 border-l-2 border-green-400" style={{ color: 'var(--text-primary)' }}>
                            <span className="font-medium">Oplossing: </span>{problem.oplossing_omschrijving}
                          </p>
                        )}
                        <div className="text-xs mt-1.5" style={{ color: 'var(--text-tertiary)' }}>
                          {new Date(problem.datum_tijd).toLocaleString('nl-NL')}
                        </div>
                      </div>
                      <button onClick={() => reopenProblem(problem)} className="btn-secondary !px-3 !py-1.5 text-xs shrink-0">
                        Heropenen
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </details>
          )}
        </section>
      </div>

      {/* Machinestatus */}
      <div className="w-full max-w-5xl px-3 sm:px-6 mt-4">
        <section className="card p-4 sm:p-6">
          <div className="flex items-center gap-2 border-b pb-3 mb-4" style={{ borderColor: 'var(--border)' }}>
            <Wrench size={18} style={{ color: 'var(--text-secondary)' }} />
            <h2 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>Machinestatus</h2>
          </div>

          {statusError && (
            <div className="mb-3 p-3 rounded-lg bg-red-50 text-red-700 text-sm dark:bg-red-900/25 dark:text-red-300">{statusError}</div>
          )}

          {machines.length === 0 ? (
            <p className="text-sm py-4" style={{ color: 'var(--text-secondary)' }}>Geen machines gevonden.</p>
          ) : (
            <div className="space-y-2.5">
              {machines.map(machine => {
                const meta = statusMeta(machine.status)
                const saving = statusSavingId === machine.id
                return (
                  <div key={machine.id} className="p-3 rounded-xl flex flex-wrap items-center justify-between gap-3"
                    style={{ background: 'var(--surface-muted)' }}>
                    <div className="flex items-center gap-2.5 min-w-0">
                      <span className={`status-indicator ${meta.dot}`} />
                      <div className="min-w-0">
                        <div className="font-medium text-sm truncate" style={{ color: 'var(--text-primary)' }}>{machine.name}</div>
                        <div className="text-[11px]" style={{ color: 'var(--text-tertiary)' }}>
                          {[machine.hal, machine.location].filter(Boolean).join(' · ') || meta.label}
                        </div>
                      </div>
                    </div>
                    <div className="flex rounded-lg overflow-hidden border" style={{ borderColor: 'var(--border)' }}>
                      {STATUS_OPTIONS.map(opt => {
                        const Icon = opt.icon
                        const isActive = (machine.status || 'active') === opt.val
                        return (
                          <button key={opt.val} type="button" disabled={saving}
                            onClick={() => setMachineStatus(machine, opt.val)}
                            className={`px-3 py-2 text-xs font-medium flex items-center gap-1.5 transition-colors disabled:opacity-50 ${
                              isActive ? opt.activeClass : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                            }`}>
                            <Icon size={13} /> <span className="hidden sm:inline">{opt.label}</span>
                          </button>
                        )
                      })}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </section>
      </div>

      {/* Eigen taken */}
      <div className="w-full max-w-5xl px-3 sm:px-6 mt-4 mb-8">
        <section className="card p-4 sm:p-6">
          <div className="flex justify-between items-center border-b pb-3 mb-4" style={{ borderColor: 'var(--border)' }}>
            <h2 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
              Onderhoudstaken ({pendingTasks.length} open)
            </h2>
            <button onClick={() => setShowTaskForm(!showTaskForm)} className="btn-primary !px-3 touch-target">
              <Plus size={16} /> Taak
            </button>
          </div>

          {showTaskForm && (
            <div className="mb-4 p-4 rounded-xl" style={{ background: 'var(--bg)' }}>
              <input
                type="text"
                value={newTask}
                onChange={e => setNewTask(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && addTask()}
                placeholder="Bijv. smeerbeurt lijnmotor Hal 3"
                className="input-field mb-2"
              />
              <input type="time" value={scheduledTime} onChange={e => setScheduledTime(e.target.value)} className="input-field mb-3" />
              <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>Prioriteit</label>
              <div className="flex rounded-lg overflow-hidden border mb-3" style={{ borderColor: 'var(--border)' }}>
                {([
                  { val: 'laag' as const, label: 'Laag', activeClass: 'bg-gray-500 text-white' },
                  { val: 'normaal' as const, label: 'Normaal', activeClass: 'bg-yellow-500 text-white' },
                  { val: 'hoog' as const, label: 'Hoog', activeClass: 'bg-red-600 text-white' },
                ]).map(opt => (
                  <button key={opt.val} type="button" onClick={() => setNewTaskPriority(opt.val)}
                    className={`flex-1 py-2.5 text-sm font-medium transition-colors ${
                      newTaskPriority === opt.val ? opt.activeClass : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                    }`}>
                    {opt.label}
                  </button>
                ))}
              </div>
              <div className="flex gap-2">
                <button onClick={() => { setShowTaskForm(false); setNewTask(''); setScheduledTime(''); setNewTaskPriority('normaal') }}
                  className="btn-secondary flex-1 touch-target">Annuleren</button>
                <button onClick={addTask} className="btn-primary flex-1 touch-target">Toevoegen</button>
              </div>
            </div>
          )}

          <div className="space-y-2">
            {pendingTasks.length === 0 ? (
              <div className="text-center py-6" style={{ color: 'var(--text-secondary)' }}>
                <CheckCircle size={40} className="mx-auto mb-2 text-green-400" />
                <p>Geen openstaande onderhoudstaken.</p>
              </div>
            ) : (
              pendingTasks.map(task => (
                <div key={task.id} className={`p-3 rounded-xl transition-all hover:shadow-md ${priorityStyle(task.priority)}`}>
                  <div className="flex items-start gap-3">
                    <button onClick={() => toggleTask(task.id, task.completed)}
                      className="text-gray-400 hover:text-green-500 mt-0.5 touch-target flex items-center justify-center" aria-label="Taak afronden">
                      <Circle size={24} />
                    </button>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium" style={{ color: 'var(--text-primary)' }}>{task.task}</span>
                        {task.priority === 'hoog' && (
                          <span className="text-xs px-2 py-0.5 bg-red-100 text-red-700 rounded-full dark:bg-red-900/40 dark:text-red-300">Urgent</span>
                        )}
                        {task.created_by !== user?.id && (
                          <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full dark:bg-blue-900/40 dark:text-blue-300">
                            Van: {task.created_by_name}
                          </span>
                        )}
                      </div>
                      <div className="text-sm mt-1 flex items-center gap-3 flex-wrap" style={{ color: 'var(--text-secondary)' }}>
                        {task.scheduled_at && (
                          <span className="flex items-center gap-1">
                            <Clock size={14} />
                            {new Date(task.scheduled_at).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        )}
                        <span>{new Date(task.created_at).toLocaleDateString('nl-NL')}</span>
                      </div>
                    </div>
                    <button onClick={() => deleteTask(task.id)} className="text-gray-400 hover:text-red-500 touch-target flex items-center justify-center" aria-label="Taak verwijderen">
                      <Trash2 size={20} />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          {completedTasks.length > 0 && (
            <details className="mt-4">
              <summary className="cursor-pointer text-sm py-2" style={{ color: 'var(--text-secondary)' }}>
                {completedTasks.length} voltooide {completedTasks.length === 1 ? 'taak' : 'taken'} tonen
              </summary>
              <div className="mt-2 space-y-2">
                {completedTasks.map(task => (
                  <div key={task.id} className="p-3 rounded-xl bg-green-50 border-l-4 border-l-green-400 dark:bg-green-900/10">
                    <div className="flex items-start gap-3">
                      <button onClick={() => toggleTask(task.id, task.completed)} className="text-green-500 mt-0.5 touch-target flex items-center justify-center" aria-label="Terugzetten naar open">
                        <CheckCircle size={24} />
                      </button>
                      <div className="flex-1 min-w-0">
                        <span className="line-through text-gray-500 dark:text-gray-400">{task.task}</span>
                        <div className="text-xs text-gray-400 mt-1">
                          Voltooid: {task.completed_at ? new Date(task.completed_at).toLocaleString('nl-NL') : 'Onbekend'}
                        </div>
                      </div>
                      <button onClick={() => deleteTask(task.id)} className="text-gray-400 hover:text-red-500 touch-target flex items-center justify-center" aria-label="Taak verwijderen">
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </details>
          )}
        </section>
      </div>

      {/* Oplossing-modal */}
      {solving && (
        <div className="modal-overlay" onClick={() => setSolving(null)}>
          <div className="modal-content max-w-lg" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold" style={{ color: 'var(--text-primary)' }}>Storing oplossen</h3>
              <button onClick={() => setSolving(null)} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition" aria-label="Sluiten">
                <X size={18} style={{ color: 'var(--text-secondary)' }} />
              </button>
            </div>

            <div className="p-3 rounded-xl mb-4" style={{ background: 'var(--surface-muted)' }}>
              <div className="flex items-center gap-2 flex-wrap">
                <AlertTriangle size={15} className="text-orange-500" />
                <span className="font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>{machineName(solving.machine_id)}</span>
                {solving.productcode && <span className="badge badge-gray">Code {solving.productcode}</span>}
              </div>
              <p className="text-sm mt-1.5 whitespace-pre-wrap" style={{ color: 'var(--text-secondary)' }}>{solving.argumentatie}</p>
              <div className="text-xs mt-1.5" style={{ color: 'var(--text-tertiary)' }}>
                Gemeld {new Date(solving.datum_tijd).toLocaleString('nl-NL')} door {solving.gebruiker_naam || solving.created_by_name || 'onbekend'}
              </div>
            </div>

            <form onSubmit={saveSolution} className="space-y-3">
              {solutionError && (
                <div className="p-3 rounded-lg bg-red-50 text-red-700 text-sm dark:bg-red-900/25 dark:text-red-300">{solutionError}</div>
              )}
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>Wat heb je gedaan? *</label>
                <textarea required className="input-field resize-none" rows={4} value={solution}
                  onChange={e => setSolution(e.target.value)}
                  placeholder="Beschrijf de uitgevoerde reparatie of oplossing..." />
              </div>
              <div className="flex gap-2 pt-1">
                <button type="button" onClick={() => setSolving(null)} className="btn-secondary flex-1 touch-target">Annuleren</button>
                <button type="submit" disabled={solutionSaving || !solution.trim()} className="btn-primary flex-1 touch-target disabled:opacity-50">
                  {solutionSaving ? 'Opslaan...' : 'Markeer als opgelost'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
